import type { EnergyCost, OwnershipCosts, RoadTax } from '../api/types'
import { formatNumber } from './format'

/**
 * What the vehicle costs to keep — the wording, and the line between the kinds of claim in it.
 *
 * The card holds three things that look alike and are not. The tax milestones are **dates**: they
 * follow from one published rule and the vehicle's own date of first admission, and they are either
 * right or absent. The energy figure is an **estimate**, built on a national-average mileage and a
 * price that moves weekly. The road tax is a **calculation** from the law's rate tables, exact for
 * the year it names but unable to say which province applies. None may be dressed as another,
 * which is why each carries its own inputs and caveats on screen rather than only its answer.
 */

export const OWNERSHIP_TITLE = 'Wat kost deze auto om te houden?'

/**
 * The heading of the road tax block, and its name in the premium banner. One function so the two
 * cannot drift apart.
 *
 * A passenger car's amount varies by province; a van's does not, but varies by who keeps it, so the
 * heading names what the rows are listed by.
 */
export function roadTaxTitle(roadTax: RoadTax): string {
  return isVanRoadTax(roadTax)
    ? 'Motorrijtuigenbelasting voor deze bestelauto'
    : 'Motorrijtuigenbelasting per provincie'
}

/** Whether the road tax is a van's, listed per kind of keeper rather than per province. */
export function isVanRoadTax(roadTax: RoadTax): boolean {
  return roadTax.vehicleClass === 'Van'
}

/**
 * The card's subtitle, naming only what this car actually gets.
 *
 * "Brandstof of stroom" on a petrol car makes the reader wonder which applies; the estimate already
 * knows. Each part is named only when it is on the card.
 */
export function ownershipSubtitle(ownership: OwnershipCosts | undefined, roadTax: RoadTax | undefined): string {
  const parts = [
    ownership?.energy &&
      `wat een jaar rijden aan ${isElectricEnergy(ownership.energy) ? 'stroom' : 'brandstof'} kost`,
    roadTax && 'de motorrijtuigenbelasting',
    (ownership?.roadTaxExemption !== undefined || ownership?.youngtimer !== undefined) &&
      'vrijstellingen die met de leeftijd meekomen',
  ].filter((part): part is string => typeof part === 'string')

  const last = parts.at(-1) ?? ''
  const rest = parts.slice(0, -1)

  return capitalise(rest.length > 0 ? `${rest.join(', ')} en ${last}` : last)
}

/** The calendar year the road tax rates belong to, which may be last year's after 1 January. */
export function roadTaxYear(roadTax: RoadTax): number {
  return new Date(roadTax.ratesEffectiveFrom).getUTCFullYear()
}

export function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Whether the estimate was priced on electricity rather than on a fuel bought at the pump. */
export function isElectricEnergy(energy: EnergyCost): boolean {
  return energy.consumptionUnit.toLowerCase() === 'kwh'
}

/**
 * What the estimate is the cost of, in the reader's words.
 *
 * "Energiekosten" is what a Dutch reader calls the household energy bill, so beside "Benzine" it
 * reads as the wrong number. A pump fuel is "brandstofkosten" and electricity "stroomkosten".
 */
export function energyCostNoun(energy: EnergyCost): string {
  return isElectricEnergy(energy) ? 'stroomkosten' : 'brandstofkosten'
}

/** Whether there is anything to show. Each of the four parts is independently absent. */
export function hasOwnershipCosts(ownership: OwnershipCosts | undefined, roadTax: RoadTax | undefined): boolean {
  return (
    roadTax !== undefined ||
    ownership?.roadTaxExemption !== undefined ||
    ownership?.youngtimer !== undefined ||
    ownership?.energy !== undefined
  )
}

/**
 * Consumption with the unit the API measured it in.
 *
 * `consumptionUnit` is `l` for a fuel and `kWh` for electricity, and assuming litres would print
 * "17,2 l/100 km" for an electric car — a figure that is wrong by a factor nobody could spot from
 * the number alone.
 */
export function formatEnergyConsumption(energy: EnergyCost): string | undefined {
  const consumption = formatNumber(energy.consumptionPer100Km)
  return consumption && `${consumption} ${energy.consumptionUnit}/100 km`
}

/**
 * The date the configured price took effect, or `undefined` when nobody set one.
 *
 * The backend's option defaults to `0001-01-01`, so a deployment that fills in prices but forgets
 * the date would print "prijspeil 1 januari 1" beside a real figure. An absent price level is
 * quieter and more honest than an absurd one — and the caveat that prices move week to week is in
 * the disclaimers either way.
 */
export function priceLevel(energy: EnergyCost): string | undefined {
  const date = new Date(energy.pricesEffectiveFrom)

  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < 1900) {
    return undefined
  }

  return energy.pricesEffectiveFrom
}

/**
 * How long until a date that has not arrived, in whole years, or `undefined` once it has.
 *
 * The point of showing the exemption as a date rather than as "nee" is that a buyer looking at a
 * car in its thirties can count towards it. Saying how far off it is finishes that thought.
 */
export function yearsUntil(date: string, now: Date = new Date()): number | undefined {
  const target = new Date(date)

  if (Number.isNaN(target.getTime()) || target.getTime() <= now.getTime()) {
    return undefined
  }

  const years = Math.floor((target.getTime() - now.getTime()) / (365.2425 * 24 * 60 * 60 * 1000))
  return years > 0 ? years : undefined
}
